import * as React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Minimal modal dialog (no external dependency). Renders an overlay + centered
 * panel when `open`. Close on overlay click, the X button, or Escape.
 *
 * The overlay is portaled to <body> so its `position: fixed` resolves against
 * the viewport rather than the nearest transformed/overflow ancestor. Several
 * wrappers in the app carry a `transform` (e.g. the `animate-fade-up` tab
 * content), which would otherwise become the containing block for the fixed
 * overlay and clip the dialog inside that box (see the Preview/Quality tabs).
 */
function Dialog({ open, onClose, children, className }) {
  React.useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || typeof document === 'undefined') return null
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/20 p-2 backdrop-blur-[2px] sm:items-center sm:p-4">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative z-10 my-0 max-h-[calc(100dvh-1rem)] w-full max-w-lg overflow-hidden rounded-lg border border-border bg-card p-5 shadow-pop animate-pop-in sm:my-8 sm:p-6',
          className
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>,
    document.body
  )
}

const DialogHeader = ({ className, ...props }) => (
  <div className={cn('mb-4 space-y-1', className)} {...props} />
)
const DialogTitle = ({ className, ...props }) => (
  <h2 className={cn('text-lg font-semibold tracking-tight', className)} {...props} />
)
const DialogDescription = ({ className, ...props }) => (
  <p className={cn('text-sm text-muted-foreground', className)} {...props} />
)
const DialogFooter = ({ className, ...props }) => (
  <div className={cn('mt-6 flex justify-end gap-2', className)} {...props} />
)

export { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter }
